import { basicBlockActions } from 'articleEditor/utils/actions';
import {
  findNodePath,
  setNodes,
} from 'lib-knowledge-document-structure/plate/common';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  MyEditor,
  MyHtmlElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { Path } from 'lib-knowledge-document-structure/slate/package';
import { useMemo } from 'react';

import { SortableBlockWrapper } from '../baseBlock';
import s from '../baseBlock/block.module.scss';
import { HtmlBlockProps } from './htmlBlock';

type HtmlBlockValueProps = HtmlBlockProps & {
  onEditHtmlBlock: () => void;
};

export default function HtmlBlockValue({
  element,
  attributes,
  onEditHtmlBlock,
  children,
}: HtmlBlockValueProps): JSX.Element {
  const editor = useMyEditorRef();
  const path = findNodePath(editor, element);
  if (!path) throw new Error('HtmlBlockValue path is not defined'); // this cant happen, path should always be defined

  const blockActions = useMemo(
    () => createBlockActions({ element, path, editor, onEditHtmlBlock }),
    [editor, element, onEditHtmlBlock, path],
  );

  const html = useMemo(() => {
    return `<html-block src="${encodeURIComponent(element.html || '')}" use-article-styles="${element.useArticleStyles !== false ? 'true' : 'false'}"></html-block>`;
  }, [element.html, element.useArticleStyles]);

  return (
    <SortableBlockWrapper
      element={element}
      blockActions={blockActions}
      className={s['blockHtml']}
    >
      <div
        {...attributes}
        className={s['blockContent']}
        contentEditable={false}
      >
        <div dangerouslySetInnerHTML={{ __html: html }} />
        {children}
      </div>
    </SortableBlockWrapper>
  );
}

function createBlockActions({
  onEditHtmlBlock,
  editor,
  element,
  path,
}: {
  onEditHtmlBlock: () => void;
  editor: MyEditor;
  element: MyHtmlElement;
  path: Path;
}) {
  return [
    {
      key: 'replace',
      icon: 'pen' as const,
      label: 'Edit',
      isSelected: false,
      action: onEditHtmlBlock,
    },
    ...basicBlockActions,
    {
      key: 'article-css',
      icon: 'format' as const,
      label: 'Use article styles',
      // Default to adding article styles
      isSelected: element.useArticleStyles !== false,
      action: () => {
        setNodes(
          editor,
          { useArticleStyles: !element.useArticleStyles },
          { at: path },
        );
      },
    },
  ] as const;
}
